
/* ============================================================
   DMW TOPIC — MOST RECENT CAROUSEL
   ============================================================

   DESKTOP
   - Horizontal scrolling rail.
   - Centre-card detection.
   - Hover and keyboard focus activation.
   - Shopify Theme Editor block selection.

   MOBILE (768px and below)
   - Three fixed visual positions.
   - Centred active card with previous/next previews.
   - Mouse dragging and touchscreen swiping.
   - Side-card click to centre; second click follows its link.
   - Shift + mouse wheel navigation.
   - Keyboard arrow navigation.

   Styling:
   assets/dmw-topic-most-recent.css
   ============================================================ */

(function () {
  'use strict';

  var SECTION_SELECTOR = '.dmw-most-recent';
  var TRACK_SELECTOR = '.dmw-most-recent__track';
  var CARD_SELECTOR = '.dmw-most-recent__card';
  var MOBILE_QUERY = '(max-width: 768px)';
  var SWIPE_DISTANCE = 35;

  var sectionStates = new WeakMap();

  /* Set the active card and its two circular neighbours. */
  function setActiveCard(cards, activeCard) {
    if (!cards.length || !activeCard) return;

    var activeIndex = cards.indexOf(activeCard);
    if (activeIndex === -1) return;

    var previousIndex = (activeIndex - 1 + cards.length) % cards.length;
    var nextIndex = (activeIndex + 1) % cards.length;

    cards.forEach(function (card, index) {
      card.classList.toggle('is-active', index === activeIndex);
      card.classList.toggle(
        'is-prev',
        cards.length > 1 && index === previousIndex
      );
      card.classList.toggle(
        'is-next',
        cards.length > 1 && index === nextIndex
      );
    });
  }

  /* Desktop: find the card nearest the horizontal track centre. */
  function getClosestCardToCentre(track, cards) {
    if (!track || !cards.length) return null;

    var trackRect = track.getBoundingClientRect();
    var trackCentre = trackRect.left + trackRect.width / 2;
    var closestCard = null;
    var closestDistance = Infinity;

    cards.forEach(function (card) {
      var cardRect = card.getBoundingClientRect();
      var cardCentre = cardRect.left + cardRect.width / 2;
      var distance = Math.abs(trackCentre - cardCentre);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestCard = card;
      }
    });

    return closestCard;
  }

  function initializeSection(section) {
    if (!section || section.dataset.dmwMostRecentInitialized === 'true') {
      return;
    }

    var track = section.querySelector(TRACK_SELECTOR);
    if (!track) return;

    var cards = Array.prototype.slice.call(
      track.querySelectorAll(CARD_SELECTOR)
    );

    if (!cards.length) return;

    section.dataset.dmwMostRecentInitialized = 'true';

    var animationFrame = null;
    var hoveredCard = null;
    var focusedCard = null;
    var editorSelectedCard = null;

    var mobileIndex = cards.length > 1 ? 1 : 0;
    var mouseStart = null;
    var touchStart = null;
    var suppressSwipeClick = false;
    var lastMobileWheel = 0;

    function isMobileRail() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    function showMobileCard(index) {
      if (!isMobileRail()) return;

      mobileIndex = (index + cards.length) % cards.length;
      setActiveCard(cards, cards[mobileIndex]);
    }

    /* Mobile uses its own active index; desktop retains its priorities. */
    function getPreferredActiveCard() {
      if (isMobileRail()) return cards[mobileIndex];

      if (focusedCard && track.contains(focusedCard)) {
        return focusedCard;
      }

      if (hoveredCard && track.contains(hoveredCard)) {
        return hoveredCard;
      }

      if (editorSelectedCard && track.contains(editorSelectedCard)) {
        return editorSelectedCard;
      }

      return getClosestCardToCentre(track, cards);
    }

    function updateActiveCard() {
      var card = getPreferredActiveCard();
      if (card) setActiveCard(cards, card);
    }

    function queueActiveCardUpdate() {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      animationFrame = requestAnimationFrame(function () {
        animationFrame = null;
        updateActiveCard();
      });
    }

    /* Preserve desktop rail scrolling. */
    track.addEventListener('scroll', queueActiveCardUpdate, {
      passive: true
    });

    /* Desktop hover behaviour. */
    cards.forEach(function (card) {
      card.addEventListener('pointerenter', function () {
        hoveredCard = card;
        updateActiveCard();
      });

      card.addEventListener('pointerleave', function () {
        if (hoveredCard === card) {
          hoveredCard = null;
          queueActiveCardUpdate();
        }
      });
    });

    /* Keyboard focus behaviour. */
    track.addEventListener('focusin', function (event) {
      var card = event.target.closest(CARD_SELECTOR);

      if (card && track.contains(card)) {
        focusedCard = card;
        updateActiveCard();
      }
    });

    track.addEventListener('focusout', function () {
      window.setTimeout(function () {
        var element = document.activeElement;
        var card = element && element.closest
          ? element.closest(CARD_SELECTOR)
          : null;

        focusedCard = card && track.contains(card) ? card : null;
        queueActiveCardUpdate();
      }, 0);
    });

    /* ----------------------------------------------------------
       MOBILE MOUSE DRAG AND TOUCHSCREEN SWIPE

       Mouse and touch events are handled separately rather than
       relying on Pointer Event emulation in Shopify's Theme Editor.

       Gesture starts are captured at document level. Movement and
       release are tracked globally so the gesture continues even
       when the pointer leaves the original card.

       Horizontal gestures rotate the carousel. Vertical touch
       movement remains available for normal page scrolling.

       A completed gesture suppresses its follow-up click so that
       a swipe does not accidentally open the card link.
       ---------------------------------------------------------- */

    function getCardFromTarget(target) {
      if (!target || !target.closest) return null;

      var card = target.closest(CARD_SELECTOR);

      return card && track.contains(card) ? card : null;
    }

    function getTouchByIdentifier(list, identifier) {
      for (var i = 0; i < list.length; i++) {
        if (list[i].identifier === identifier) {
          return list[i];
        }
      }

      return null;
    }

    function finishGesture(start, x, y) {
      if (!start) return;

      var dx = x - start.x;
      var dy = y - start.y;

      if (
        isMobileRail() &&
        Math.abs(dx) >= SWIPE_DISTANCE &&
        Math.abs(dx) > Math.abs(dy) * 1.2
      ) {
        suppressSwipeClick = true;

        showMobileCard(
          mobileIndex + (dx < 0 ? 1 : -1)
        );

        window.setTimeout(function () {
          suppressSwipeClick = false;
        }, 600);
      }
    }

    /* Mouse drag: supports testing the mobile layout on desktop. */
    function onMouseDown(event) {
      if (!isMobileRail() || event.button !== 0) return;
      if (!getCardFromTarget(event.target)) return;

      mouseStart = {
        x: event.clientX,
        y: event.clientY
      };
    }

    function onMouseMove(event) {
      if (!mouseStart || !isMobileRail()) return;

      var dx = event.clientX - mouseStart.x;
      var dy = event.clientY - mouseStart.y;

      if (
        Math.abs(dx) > 8 &&
        Math.abs(dx) > Math.abs(dy) &&
        event.cancelable
      ) {
        event.preventDefault();
      }
    }

    function onMouseUp(event) {
      if (!mouseStart) return;

      var start = mouseStart;
      mouseStart = null;

      finishGesture(start, event.clientX, event.clientY);
    }

    /* Touch swipe: supports real phones and browser emulation. */
    function onTouchStart(event) {
      if (!isMobileRail() || event.touches.length !== 1) return;
      if (!getCardFromTarget(event.target)) return;

      var touch = event.touches[0];

      touchStart = {
        x: touch.clientX,
        y: touch.clientY,
        identifier: touch.identifier
      };
    }

    function onTouchMove(event) {
      if (!touchStart || !isMobileRail()) return;

      var touch = getTouchByIdentifier(
        event.touches,
        touchStart.identifier
      );

      if (!touch) return;

      var dx = touch.clientX - touchStart.x;
      var dy = touch.clientY - touchStart.y;

      if (
        Math.abs(dx) > 8 &&
        Math.abs(dx) > Math.abs(dy) &&
        event.cancelable
      ) {
        event.preventDefault();
      }
    }

    function onTouchEnd(event) {
      if (!touchStart) return;

      var touch = getTouchByIdentifier(
        event.changedTouches,
        touchStart.identifier
      );

      if (!touch) return;

      var start = touchStart;
      touchStart = null;

      finishGesture(start, touch.clientX, touch.clientY);
    }

    function cancelGestures() {
      mouseStart = null;
      touchStart = null;
    }

    /* Capture gesture starts before child links handle them. */
    document.addEventListener('mousedown', onMouseDown, true);

    document.addEventListener('touchstart', onTouchStart, {
      capture: true,
      passive: true
    });

    /* Track movement and release outside the original card. */
    document.addEventListener('mousemove', onMouseMove, true);
    document.addEventListener('mouseup', onMouseUp, true);

    document.addEventListener('touchmove', onTouchMove, {
      capture: true,
      passive: false
    });

    document.addEventListener('touchend', onTouchEnd, true);
    document.addEventListener('touchcancel', cancelGestures, true);

    window.addEventListener('blur', cancelGestures);

    /* Prevent native image/link dragging replacing carousel gestures. */
    track.addEventListener('dragstart', function (event) {
      if (isMobileRail()) {
        event.preventDefault();
      }
    });

    /* Side-card clicks centre the card; centred-card clicks navigate. */
    track.addEventListener('click', function (event) {
      if (!isMobileRail()) return;

      if (suppressSwipeClick) {
        suppressSwipeClick = false;
        event.preventDefault();
        event.stopPropagation();

        if (event.stopImmediatePropagation) {
          event.stopImmediatePropagation();
        }

        return;
      }

      var card = event.target.closest(CARD_SELECTOR);

      if (!card || !track.contains(card)) return;

      var index = cards.indexOf(card);

      if (index !== mobileIndex) {
        event.preventDefault();
        event.stopPropagation();
        showMobileCard(index);
      }
    }, true);

    /* Shift + mouse wheel and genuine horizontal wheel navigation. */
    track.addEventListener('wheel', function (event) {
      if (!isMobileRail()) return;

      var horizontalWheel = Math.abs(event.deltaX) > 1;

      if (!event.shiftKey && !horizontalWheel) return;

      var delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;

      if (Math.abs(delta) < 1) return;

      event.preventDefault();

      var now = Date.now();

      if (now - lastMobileWheel < 350) return;

      lastMobileWheel = now;

      showMobileCard(
        mobileIndex + (delta > 0 ? 1 : -1)
      );
    }, { passive: false });

    /* Keyboard arrows rotate cards without following their links. */
    track.addEventListener('keydown', function (event) {
      if (!isMobileRail()) return;

      if (
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight'
      ) {
        return;
      }

      event.preventDefault();

      showMobileCard(
        mobileIndex + (event.key === 'ArrowRight' ? 1 : -1)
      );

      var link = cards[mobileIndex].querySelector('a');

      if (link) {
        link.focus({ preventScroll: true });
      }
    });

    /* Responsive layout changes. */
    var resizeObserver = null;

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(queueActiveCardUpdate);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', queueActiveCardUpdate);
    }

    /* Shopify Theme Editor block selection and cleanup. */
    sectionStates.set(section, {
      selectEditorCard: function (card) {
        if (!card || !track.contains(card)) return;

        editorSelectedCard = card;

        if (isMobileRail()) {
          showMobileCard(cards.indexOf(card));
        } else {
          updateActiveCard();
        }
      },

      deselectEditorCard: function (card) {
        if (!card || editorSelectedCard === card) {
          editorSelectedCard = null;
          queueActiveCardUpdate();
        }
      },

      cleanup: function () {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }

        if (resizeObserver) {
          resizeObserver.disconnect();
        } else {
          window.removeEventListener('resize', queueActiveCardUpdate);
        }

        document.removeEventListener('mousedown', onMouseDown, true);
        document.removeEventListener('touchstart', onTouchStart, true);
        document.removeEventListener('mousemove', onMouseMove, true);
        document.removeEventListener('mouseup', onMouseUp, true);
        document.removeEventListener('touchmove', onTouchMove, true);
        document.removeEventListener('touchend', onTouchEnd, true);
        document.removeEventListener('touchcancel', cancelGestures, true);

        window.removeEventListener('blur', cancelGestures);

        mouseStart = null;
        touchStart = null;

        section.dataset.dmwMostRecentInitialized = 'false';
      }
    });

    queueActiveCardUpdate();
  }

  /* Initialize current sections and sections reloaded by Shopify. */
  function initializeAllSections(root) {
    var scope = root || document;

    if (
      scope.matches &&
      scope.matches(SECTION_SELECTOR)
    ) {
      initializeSection(scope);
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll(SECTION_SELECTOR).forEach(function (section) {
        initializeSection(section);
      });
    }
  }

  function start() {
    initializeAllSections(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Shopify Theme Editor section lifecycle. */
  document.addEventListener('shopify:section:load', function (event) {
    initializeAllSections(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target.matches &&
      event.target.matches(SECTION_SELECTOR)
      ? event.target
      : event.target.querySelector
        ? event.target.querySelector(SECTION_SELECTOR)
        : null;

    if (!section) return;

    var state = sectionStates.get(section);

    if (state && state.cleanup) {
      state.cleanup();
    }

    sectionStates.delete(section);
  });

  /* Shopify Theme Editor block selection events. */
  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target.closest
      ? event.target.closest(CARD_SELECTOR)
      : null;

    if (!card) return;

    var section = card.closest(SECTION_SELECTOR);
    var state = section && sectionStates.get(section);

    if (state && state.selectEditorCard) {
      state.selectEditorCard(card);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var card = event.target.closest
      ? event.target.closest(CARD_SELECTOR)
      : null;

    if (!card) return;

    var section = card.closest(SECTION_SELECTOR);
    var state = section && sectionStates.get(section);

    if (state && state.deselectEditorCard) {
      state.deselectEditorCard(card);
    }
  });

})();
